// client/src/utils/analytics.js
// =============================================================================
// Google Analytics 4 (GA4) — 單一來源
// -----------------------------------------------------------------------------
// 設計重點：
//  1. ID 抽環境變數：優先讀 REACT_APP_GA_MEASUREMENT_ID，沒設則用下方 FALLBACK_ID。
//  2. 環境分流：只有 ENABLED_HOSTS 內的網域才會真的載入並送資料。
//     localhost / test 機 / 任何其他網域一律「不載入、不送」，正式數據不被污染。
//     （刻意不用 NODE_ENV 判斷：test 與 prod 都是 production build，分不出來；
//       用 hostname 最可靠。）
//  3. SPA 追蹤：config 時關掉 send_page_view，page_view 一律由 route 變更手動送
//     （見 App.js 的 useLocation effect → trackPageView），避免首次載入與切頁重複計數。
//
// 要換 ID：改下方 FALLBACK_ID，或設環境變數 REACT_APP_GA_MEASUREMENT_ID。
// 要讓 test 站也送：把 test 網域加進 ENABLED_HOSTS（或設 REACT_APP_GA_ENABLED_HOSTS）。
// =============================================================================

const FALLBACK_ID = "G-CFWE7BV2LH"; // trade.masquant.com 正式 GA4 資源

export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || FALLBACK_ID;

// 允許送 GA 的正式網域（逗號分隔，可用環境變數覆蓋）
const ENABLED_HOSTS = (process.env.REACT_APP_GA_ENABLED_HOSTS || "trade.masquant.com")
  .split(",")
  .map((h) => h.trim())
  .filter(Boolean);

const currentHost = typeof window !== "undefined" ? window.location.hostname : "";

// 啟用條件：有 ID + 目前網域在白名單內
export const isGAEnabled = !!GA_MEASUREMENT_ID && ENABLED_HOSTS.includes(currentHost);

let initialized = false;

/**
 * 啟動 GA4：動態注入 gtag.js 並初始化。
 * 只在正式網域執行；自帶防重，可重複呼叫。
 */
export function initGA() {
  if (initialized || !isGAEnabled) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  // SPA：關掉自動 page_view，改由 route 變更手動送（見 trackPageView）
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
}

/**
 * 送一次 page_view。SPA 切頁時呼叫（含首次載入）。
 * @param {string} path 例如 location.pathname
 */
export function trackPageView(path) {
  if (!isGAEnabled || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * 送自訂事件（轉換追蹤用）。
 * @param {string} name 事件名，建議用 GA4 慣例 snake_case，如 sign_up / purchase
 * @param {object} [params] 事件參數
 */
export function trackEvent(name, params = {}) {
  if (!isGAEnabled || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}
